import { AppliedCreditNote, CurrentPayment } from 'Invoices/types'
import { sumAppliedCreditNotes, sumCurrentPayments } from 'Invoices/utils'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'

export type CreditNoteTotals = {
  subtotal: number
  tax: number
  /** Gross credit (subtotal + tax) — the amount actually issued and applied. */
  total: number
  /** Gross total of the invoice being credited. */
  original: number
  /** Total already applied to the invoice across all credit notes. */
  previouslyCredited: number
  /**
   * Amount still creditable after this draft credit note, floored at zero —
   * a "how much is left" figure can't meaningfully be negative.
   */
  remainingCreditable: number
  /**
   * Invoice amount due after the credit is applied (creditable base − total).
   * Unlike remainingCreditable this goes negative when the credit exceeds the
   * creditable base, so it can carry the error styling.
   */
  adjusted: number
  /**
   * True when the credit would exceed the invoice's outstanding balance. A
   * credit equal to the balance (adjusted === 0) is permitted; only a strictly
   * greater credit is blocked.
   */
  exceedsCreditable: boolean
  /**
   * True when there is nothing to credit (total rounds to zero, e.g. all rows
   * unchecked). A zero-value credit note must not be issuable.
   */
  isZeroCredit: boolean
  /**
   * True when the credit total is below zero. Rows accept negative amounts by
   * design, so this is the floor to the `exceedsCreditable` ceiling; it mirrors
   * the backend finalisation check.
   */
  isNegativeCredit: boolean
}

export type CreditedInvoice = {
  /** Gross total of the invoice being credited. */
  grossTotal: string
  /** Outstanding amount owed on the invoice, from the ledger. */
  balance?: string
  appliedCreditNotes: AppliedCreditNote[]
  payments?: Array<Pick<CurrentPayment, 'amount'>>
}

/**
 * Single source of truth for the issue-credit-note totals. Used by both the
 * drawer (to gate the "Issue and send" button) and the totals section (to
 * render the figures and the validation message), so the button state and the
 * displayed adjusted amount can never disagree.
 */
export const getCreditNoteTotals = (
  rows: CreditNoteLineItemRow[],
  invoice: CreditedInvoice
): CreditNoteTotals => {
  const { subtotal, tax } = rows.reduce(
    (acc, row) => {
      if (!row.checked) return acc

      const net = Number(row.price || 0)

      return {
        subtotal: acc.subtotal + net,
        tax: acc.tax + net * Number(row.taxRate || 0)
      }
    },
    { subtotal: 0, tax: 0 }
  )

  const total = subtotal + tax
  const original = Number(invoice.grossTotal || 0)

  const previouslyCredited = Number(
    sumAppliedCreditNotes(invoice.appliedCreditNotes)
  )
  const previouslyPaid = Number(sumCurrentPayments(invoice.payments))

  // The ledger balance nets out previously applied credit notes AND payments,
  // so it is the true cap on further crediting. The field is optional on the
  // API, so fall back to original − previously credited − previously paid when
  // it hasn't loaded (falling back to the raw total could over-credit a
  // partially settled invoice).
  const creditableBase =
    invoice.balance !== undefined
      ? Number(invoice.balance)
      : original - previouslyCredited - previouslyPaid

  // Work in whole cents for the comparisons and the adjusted figure. Raw float
  // arithmetic (e.g. 0.1 + 0.2) otherwise leaks noise like -5.55e-17, which a
  // full credit would render as "-$0.00", and could tip an exactly-equal
  // credit into the "exceeds" branch.
  const totalCents = Math.round(total * 100)
  const creditableBaseCents = Math.round(creditableBase * 100)

  const adjusted = (creditableBaseCents - totalCents) / 100
  const remainingCreditable = Math.max(0, adjusted)
  const exceedsCreditable = totalCents > creditableBaseCents
  const isZeroCredit = totalCents === 0
  const isNegativeCredit = totalCents < 0

  return {
    subtotal,
    tax,
    total,
    original,
    previouslyCredited,
    remainingCreditable,
    adjusted,
    exceedsCreditable,
    isZeroCredit,
    isNegativeCredit
  }
}
